import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { TwoFactorVerify } from './Auth/TwoFactorVerify';
import { Eye, EyeOff, Loader2, ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import { useRecaptcha } from '../hooks/useRecaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const { login, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const captcha = useRecaptcha();

  // Show session expired message if redirected due to token expiration
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      toast.error('Your session has expired. Please log in again.', { duration: 5000 });
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captcha.enabled && !captcha.token) {
      toast.error('Please complete the captcha challenge');
      return;
    }
    setLoading(true);

    try {
      await login({ email, password, recaptcha_token: captcha.token });
      toast.success('Welcome back!');
      
      const savedForm = localStorage.getItem('guest_form_draft');
      if (savedForm) {
        try {
          const parsed = JSON.parse(savedForm);
          if (parsed.fields && parsed.fields.length > 0) {
            localStorage.setItem('create_form_from_draft', 'true');
            navigate('/dashboard?import_draft=true');
            return;
          }
        } catch (e) {
          // Draft JSON is corrupted, ignore it
        }
      }
      navigate('/dashboard');
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed. Please try again.';
      const statusCode = error.response?.status;
      captcha.reset();
      
      if (statusCode === 202 || errorDetail.includes('Two-factor authentication required')) {
        setShow2FA(true);
        toast.info('Check your email for the OTP code');
        setLoading(false);
        return;
      }
      
      if (statusCode === 403 && errorDetail.includes('not verified')) {
        toast.error('Please verify your email before logging in.');
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else if (statusCode === 403 && errorDetail.includes('deactivated')) {
        toast.error('Your account has been deactivated. Please contact support.');
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = async (data) => {
    await refreshUser();
    
    const savedForm = localStorage.getItem('guest_form_draft');
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm);
        if (parsed.fields && parsed.fields.length > 0) {
          localStorage.setItem('create_form_from_draft', 'true');
          navigate('/dashboard?import_draft=true');
          return;
        }
      } catch (e) {}
    }
    navigate('/dashboard');
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/api/auth/google/login`;
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API_URL}/api/auth/microsoft/login`;
  };

  // Show 2FA verification screen
  if (show2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4">
        <div className="w-full max-w-md">
          <TwoFactorVerify 
            email={email}
            onBack={() => setShow2FA(false)}
            onSuccess={handle2FASuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-400/10 rounded-full blur-2xl" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo with frosted glass background */}
          <Link to="/" className="flex items-center space-x-3 mb-12 w-fit">
            <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl border border-white/30 shadow-lg">
              <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
              <span className="text-2xl font-bold text-white">Formlio</span>
            </div>
          </Link>

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            Build forms that<br />
            <span className="text-indigo-200">convert & delight</span>
          </h1>
          
          <p className="text-indigo-100 text-lg mb-10 max-w-md">
            Join thousands of creators using Formlio to build beautiful, smart forms without code.
          </p>

          {/* Feature List */}
          <div className="space-y-5">
            <div className="flex items-center space-x-4 text-white/90">
              <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Drag & Drop Builder</p>
                <p className="text-sm text-indigo-200">Create forms in minutes, not hours</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-white/90">
              <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Enterprise Security</p>
                <p className="text-sm text-indigo-200">Secure with data encryption</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-white/90">
              <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Powerful Integrations</p>
                <p className="text-sm text-indigo-200">Connect with 50+ tools you love</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center space-x-2 mb-8">
            <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
            <span className="text-xl font-bold text-slate-900">Formlio</span>
          </Link>

          {/* Back to Home Link */}
          <Link 
            to="/" 
            className="flex items-center text-slate-500 hover:text-indigo-600 text-sm mb-6 transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to website
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="login-heading">
              Welcome back
            </h2>
            <p className="text-slate-500">Sign in to continue to your dashboard</p>
          </div>

          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 font-medium transition-all duration-200"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 font-medium transition-all duration-200"
              onClick={handleMicrosoftLogin}
              data-testid="microsoft-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              Continue with Microsoft
            </Button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-400 font-medium">or</span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 transition-all"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 pr-12 transition-all"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {captcha.enabled && (
              <div className="pt-2" data-testid="login-captcha">
                <div ref={captcha.containerRef} />
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-all duration-200 group"
              disabled={loading || (captcha.enabled && !captcha.token)}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                data-testid="signup-link"
              >
                Sign up for free
              </Link>
            </p>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 flex items-center justify-center space-x-2 text-slate-400 text-sm">
            <Shield className="w-4 h-4" />
            <span>256-bit SSL encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};
